/**
 * Shape limit utilities for enforcing plan-based restrictions
 */

import { useUpgradeModalStore } from '../stores/upgradeModalStore';

export interface ShapeLimitConfig {
  maxShapes: number | null; // null means unlimited
  currentCount: number;
}

export interface ShapeLimitResult {
  canAdd: boolean;
  reason?: string;
  remainingShapes?: number;
}

interface ShapeLimitEntitlements {
  capabilities?: {
    maxShapesPerDiagram?: number | null;
  };
}

/**
 * Check if a new shape can be added based on current limits
 */
export function checkShapeLimit(config: ShapeLimitConfig): ShapeLimitResult {
  const { maxShapes, currentCount } = config;
  
  // If unlimited, always allow
  if (maxShapes === null) {
    return { canAdd: true };
  }
  
  // Check if adding one more shape would exceed the limit
  const newCount = currentCount + 1;
  if (newCount > maxShapes) {
    return {
      canAdd: false,
      reason: `Adding this shape would exceed your limit of ${maxShapes} shapes. Please upgrade to add more shapes.`,
      remainingShapes: Math.max(0, maxShapes - currentCount)
    };
  }
  
  return {
    canAdd: true,
    remainingShapes: Math.max(0, maxShapes - currentCount)
  };
}

/**
 * Get the shape limit for the current plan
 * This would typically be called from a component that has access to entitlements
 */
export function getShapeLimitFromEntitlements(entitlements: ShapeLimitEntitlements | null | undefined): number | null {
  if (entitlements?.capabilities?.maxShapesPerDiagram == null) {
    return 0; // Unresolved entitlements fail closed.
  }
  
  const limit = entitlements.capabilities.maxShapesPerDiagram;
  return limit === Number.MAX_SAFE_INTEGER ? null : limit;
}

/**
 * Validates shape limit before adding new shapes and shows upgrade modal if limit exceeded.
 * 
 * @param store - The diagram store instance
 * @param shapesToAdd - Number of shapes being added (default: 1)
 * @returns true if shapes can be added, false if limit exceeded
 */
export function validateShapeLimitOrPromptUpgrade(
  store: { 
    shapeLimitChecker?: (config: ShapeLimitConfig) => ShapeLimitResult;
    isInitialLoading?: boolean;
    nodes: readonly unknown[];
    edges: readonly unknown[];
  },
  shapesToAdd: number = 1
): boolean {
  // Initial Yjs hydration is not a user mutation. Once loading completes,
  // an absent checker means entitlements are unresolved and must fail closed.
  if (store.isInitialLoading) {
    return true;
  }

  if (!store.shapeLimitChecker) {
    const { openModal } = useUpgradeModalStore.getState();
    openModal('shapes.unlimited', 'Your plan permissions are still loading. Please try again.');
    return false;
  }

  const currentShapeCount = store.nodes.length + store.edges.length;
  const limitConfig: ShapeLimitConfig = {
    maxShapes: Number.MAX_SAFE_INTEGER, // Default to unlimited, will be overridden by entitlements
    currentCount: currentShapeCount // Pass current count, let the checker handle the addition
  };

  const limitResult = store.shapeLimitChecker(limitConfig);

  // If unlimited, always allow
  if (limitResult.canAdd && limitResult.remainingShapes === undefined) {
    return true;
  }
  
  // For multiple shapes (paste operation), we need to check if we can add all of them
  if (!limitResult.canAdd && shapesToAdd === 1) {
    const { openModal } = useUpgradeModalStore.getState();
    openModal('shapes.unlimited', limitResult.reason || 'Upgrade required to add more shapes.');
    return false;
  }
  
  // For paste operations with multiple shapes, check remaining capacity
  if (shapesToAdd > 1) {
    const remaining = limitResult.remainingShapes ?? Number.MAX_SAFE_INTEGER;
    if (remaining < shapesToAdd) {
      const { openModal } = useUpgradeModalStore.getState();
      openModal('shapes.unlimited', limitResult.reason || 'Upgrade required to add more shapes.');
      return false;
    }
  }

  return true;
}

/**
 * Validates a final scene shape count before an all-or-nothing import/replace.
 *
 * `checkShapeLimit` answers "can I add one more from this count?", while import
 * flows already know their final node+edge count. Check from zero capacity and
 * compare the reported remaining capacity to the target total so callers can
 * fail before any scene mutation happens.
 */
export function validateFinalShapeCountOrPromptUpgrade(
  store: {
    shapeLimitChecker?: (config: ShapeLimitConfig) => ShapeLimitResult;
    isInitialLoading?: boolean;
  },
  finalShapeCount: number,
): boolean {
  if (store.isInitialLoading) {
    return true;
  }

  if (!store.shapeLimitChecker) {
    const { openModal } = useUpgradeModalStore.getState();
    openModal('shapes.unlimited', 'Your plan permissions are still loading. Please try again.');
    return false;
  }

  const limitResult = store.shapeLimitChecker({
    maxShapes: Number.MAX_SAFE_INTEGER,
    currentCount: 0,
  });

  if (limitResult.canAdd && limitResult.remainingShapes === undefined) {
    return true;
  }

  const remaining = limitResult.remainingShapes ?? Number.MAX_SAFE_INTEGER;
  if (remaining < finalShapeCount) {
    const { openModal } = useUpgradeModalStore.getState();
    openModal('shapes.unlimited', limitResult.reason || 'Upgrade required to add more shapes.');
    return false;
  }

  return true;
}
